import { useEffect } from 'react';

import { usePDF } from '@react-pdf/renderer';

import { useAppSelector } from 'src/app/store/hooks';
import {
  TripEventRead,
  useOrganizationSettingsRetrieveQuery,
} from 'src/common/external/bambi-api/bambiApi';
import { selectCurrentOrganization } from 'src/features/auth/selectors';

import { MASReportRecord } from '../../../management/download/details/mas/types';
import { useMASReportContext } from '../MASReportContext';
import { MASReportDocument } from './MASReportDocument';

interface MASReportRendererProps {
  trips: MASReportRecord[];
  tripEvents: Record<string, TripEventRead[]>;
  onDocumentReady: (blob: Blob | null) => void;
}

/**
 * Renders the report document using react-pdf, triggers onDocumentReady after
 * the pdf finishes rendering as a blob.
 */
export function MASReportRenderer({
  trips,
  tripEvents,
  onDocumentReady,
}: MASReportRendererProps) {
  const { hasMapData, generateMapStructure, mapData } = useMASReportContext();
  const newMapData = generateMapStructure(trips);
  const [instance, updatePdf] = usePDF();
  const currentOrganization = useAppSelector(selectCurrentOrganization);
  const { data: organizationSettings } = useOrganizationSettingsRetrieveQuery(
    {},
    {
      skip: !currentOrganization?.id,
    }
  );

  useEffect(() => {
    if (!hasMapData(newMapData)) {
      return;
    }

    updatePdf(
      <MASReportDocument
        trips={trips}
        tripEvents={tripEvents}
        mapData={mapData}
        companyName={currentOrganization?.name || ''}
        companyLogoUrl={organizationSettings?.logo}
      />
    );
  }, [
    hasMapData,
    newMapData,
    trips,
    updatePdf,
    mapData,
    currentOrganization?.name,
    organizationSettings?.logo,
    tripEvents,
  ]);

  useEffect(() => {
    if (hasMapData(newMapData) && !instance.loading && instance.blob) {
      onDocumentReady(instance.blob);
    }
  }, [
    hasMapData,
    instance.blob,
    instance.loading,
    newMapData,
    onDocumentReady,
  ]);

  return null;
}
